/**
 * Backend Authentication Provider
 * Real implementation that authenticates against the portal-backend using JWT.
 * All environments (local, develop, staging, production) use real JWT authentication.
 */

import type { AuthProvider, LoginCredentials, AuthResult } from './auth-provider';
import type { PortalUser } from '../../api/portal-user';
import { userTypeToRole } from '../../api/portal-user';
import { useAuthStore } from './auth-store';
import { getBackendApiUrl } from '../../config/environment';

const TOKEN_STORAGE_KEY = 'ponder-portal-auth-token';
const USER_STORAGE_KEY = 'ponder-portal-auth-user';

export interface BackendLoginResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  user: {
    id: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    /** Absent for family-created students (no email) */
    email?: string;
    username?: string;
    nakamaCustomId: string;
    userType: string;
    emailVerified: boolean;
    avatarPhotoUrl?: string | null;
  };
}

export class BackendAuthProvider implements AuthProvider {
  private currentUser: PortalUser | null = null;
  private sessionRevision = 0;

  constructor() {
    this.restoreSession();
  }

  async login(credentials: LoginCredentials): Promise<AuthResult> {
    const revision = ++this.sessionRevision;
    try {
      const response = await fetch(`${getBackendApiUrl()}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          mode: credentials.mode,
          captchaToken: credentials.captchaToken,
        }),
      });

      if (revision !== this.sessionRevision) return { success: false };

      if (!response.ok) {
        return { success: false, error: 'invalid_credentials' };
      }

      const data: BackendLoginResponse = await response.json();
      if (revision !== this.sessionRevision) return { success: false };
      // Verification does not publish a session. The service first empties
      // the previous identity's cache/stores, then adopts this response.
      return { success: true, session: data };
    } catch (error) {
      if (revision !== this.sessionRevision) return { success: false };
      // Thrown by fetch itself: the request never reached the server, so the
      // credentials were never judged. Reporting this as a bad password sends
      // users off retyping details that were correct all along.
      console.error('[BackendAuthProvider] Login error:', error);
      return { success: false, error: 'network_error' };
    }
  }

  /**
   * Publishes a verified login response after service-owned session teardown:
   * builds the PortalUser, persists the session and updates the auth token.
   * Returns the user for the service to publish into the auth store.
   */
  adoptSession(data: BackendLoginResponse): PortalUser {
    this.cancelPendingRequests();
    const user: PortalUser = {
      id: data.user.id,
      name:
        data.user.name || data.user.username || data.user.email?.split('@')[0] || 'User',
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      email: data.user.email,
      nakamaCustomId: data.user.nakamaCustomId,
      role: userTypeToRole(data.user.userType),
      userType: data.user.userType,
      emailVerified: data.user.emailVerified,
      avatarPhotoUrl: data.user.avatarPhotoUrl ?? null,
    };

    this.currentUser = user;
    this.saveSession(data.accessToken, user);
    useAuthStore.getState().setToken(data.accessToken);
    return user;
  }

  async logout(): Promise<void> {
    this.cancelPendingRequests();
    this.currentUser = null;
    this.clearSession();
    useAuthStore.getState().setToken(null);
  }

  async validateSession(): Promise<boolean> {
    const revision = ++this.sessionRevision;
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token || !this.currentUser) return false;
    // Token equality alone is insufficient: the same account can sign out and
    // sign in again with the same token while an earlier validation waits.
    const isCurrent = () => revision === this.sessionRevision &&
      token === localStorage.getItem(TOKEN_STORAGE_KEY);

    try {
      const response = await fetch(`${getBackendApiUrl()}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!isCurrent()) return false;

      if (!response.ok) {
        this.currentUser = null;
        this.clearSession();
        useAuthStore.getState().setToken(null);
        return false;
      }

      // Refresh user object with latest backend data
      const fresh = await response.json() as {
        id: string;
        name?: string;
        firstName?: string;
        lastName?: string;
        email: string;
        username?: string;
        nakamaCustomId?: string;
        userType: string;
        emailVerified: boolean;
        avatarPhotoUrl?: string | null;
      };

      if (!isCurrent()) return false;

      if (this.currentUser) {
        this.currentUser = {
          ...this.currentUser,
          name: fresh.name || this.currentUser.name,
          firstName: fresh.firstName ?? this.currentUser.firstName,
          lastName: fresh.lastName ?? this.currentUser.lastName,
          nakamaCustomId: fresh.nakamaCustomId ?? this.currentUser.nakamaCustomId,
          userType: fresh.userType,
          role: userTypeToRole(fresh.userType),
          // Presence, not truthiness — every sibling field above falls back to
          // the stored value, and `?? null` would erase a photo whenever the
          // key is simply absent: an older backend that predates this field, or
          // a /auth/me whose `validateUser` missed. Testing `in` still honours a
          // deliberate clear, which arrives as an explicit null.
          avatarPhotoUrl:
            'avatarPhotoUrl' in fresh ? (fresh.avatarPhotoUrl ?? null) : this.currentUser.avatarPhotoUrl,
        };
        this.saveSession(token, this.currentUser);
      }

      useAuthStore.getState().setToken(token);
      return true;
    } catch {
      // design-checks:failure-as-answer — superseded requests cannot validate the replacement session.
      if (!isCurrent()) return false;
      // Network error — keep session optimistically so offline users aren't logged out
      useAuthStore.getState().setToken(token);
      // design-checks:failure-as-answer — the session stands until the server says otherwise.
      return true;
    }
  }

  getCurrentUser(): PortalUser | null {
    return this.currentUser;
  }

  /** Invalidate responses without clearing the token needed by leaveSession. */
  cancelPendingRequests(): void {
    this.sessionRevision++;
  }

  /**
   * Replace the cached user and its persisted copy, keeping the token.
   *
   * For changes made elsewhere in the app that the session should reflect right
   * away — editing your own profile photo. Without it the store and
   * localStorage disagree until the next /auth/me, which is a whole page load
   * of showing the previous value.
   */
  updateCurrentUser(user: PortalUser): void {
    this.cancelPendingRequests();
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    this.currentUser = user;
    if (token) this.saveSession(token, user);
  }

  private saveSession(token: string, user: PortalUser): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  }

  private restoreSession(): void {
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      const saved = localStorage.getItem(USER_STORAGE_KEY);
      if (token && saved) {
        this.currentUser = JSON.parse(saved);
        useAuthStore.getState().setToken(token);
        console.log('[BackendAuthProvider] Session restored for:', this.currentUser?.name);
      }
    } catch (error) {
      console.error('[BackendAuthProvider] Failed to restore session:', error);
      this.clearSession();
    }
  }

  private clearSession(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  }
}
